import * as React from 'react'
import { useFeatureValue, useOptionalGrowthBook } from 'growthbook'
import { isStudioDesktop } from '@repo/studio-desktop-bridge/runtime'
import { useStudioConfig } from '@repo/studio-config/studio-config-context'
import { FEATURE_PRICING_PAGE_CTA, TRIAL_CTA_VARIANTS } from '../features'
import type { TrialCtaVariant } from '../features'

/**
 * A key GrowthBook holds no feature for, so evaluating it returns the fallback
 * without running an experiment or firing the enrollment callback. Rules of
 * hooks forbid skipping the read outright, so callers outside the audience read
 * this instead.
 */
const UNENROLLED_FEATURE_KEY = ''

/**
 * Only a host whose config says `appSource: 'STUDIO'` enrols. A call site with no
 * config above it, or a config that doesn't name its app, can't tell idea.ai from
 * freemode.ai, and enrolling there puts untreated users in the treatment arm.
 *
 * Desktop, Freemode and Lumo stay out, and reading the unenrolled key keeps them
 * out of the denominator as well as off the route. Desktop's shell mounts studio's
 * home route but registers no pricing route, and Lumo's router sends `/pricing`
 * back to its own home; Freemode is a separate brand the idea.ai pricing page
 * doesn't sell.
 */
function useTrialCtaFeatureKey(): string {
  const config = useStudioConfig()
  const isStudio = config?.appSource === 'STUDIO'
  const isLumo = config?.workspaceId === 'lumo-canvas'
  const isEnrollable = isStudio && !isStudioDesktop() && !isLumo

  return isEnrollable ? FEATURE_PRICING_PAGE_CTA : UNENROLLED_FEATURE_KEY
}

function toTrialCtaVariant(value: unknown): TrialCtaVariant {
  return value === TRIAL_CTA_VARIANTS.pricingPage
    ? TRIAL_CTA_VARIANTS.pricingPage
    : TRIAL_CTA_VARIANTS.control
}

export function useTrialCtaVariant(): TrialCtaVariant {
  return toTrialCtaVariant(
    useFeatureValue<string>(useTrialCtaFeatureKey(), TRIAL_CTA_VARIANTS.control),
  )
}

/**
 * Returns a reader that evaluates the experiment only when called, so a component
 * that merely renders a paywall doesn't enroll its viewer.
 */
export function useReadTrialCtaVariant(): () => TrialCtaVariant {
  const featureKey = useTrialCtaFeatureKey()
  const growthbook = useOptionalGrowthBook()

  return React.useCallback(
    () => toTrialCtaVariant(growthbook?.getFeatureValue(featureKey, TRIAL_CTA_VARIANTS.control)),
    [growthbook, featureKey],
  )
}
